import { useCallback, useEffect, useRef } from "react";
import axios from "axios";
import { useUser } from "../../context/UserContext";
import { API } from "../../utils/constants";
import ActivityReminderService from "../../utils/ActivityReminderService";
import WebPushService from "../../utils/WebPushService";
import { Capacitor } from '@capacitor/core';
import { getSessionToken } from '../../utils/authSession';

export const getCustomActivitiesForUser = (userId) => {
  try {
    const saved = localStorage.getItem("mygoal-custom-activities");
    const customActivities = saved ? JSON.parse(saved) : [];

    if (!Array.isArray(customActivities)) return null;
    return customActivities
      .filter((activity) => activity.created_by === userId && activity.id?.startsWith('custom_') && typeof activity.date === 'string')
      .map((activity) => {
        const [year, month, day] = activity.date.split("-").map(Number);
        const [hours, minutes] = (activity.time || "18:00").split(":").map(Number);
        const startAt = new Date(year, month - 1, day, hours, minutes);

        return {
          id: activity.id,
          title: activity.title,
          location: activity.location || "",
          duration: activity.duration || 60,
          type: activity.category || activity.type || 'team_training',
          startAt,
          endAt: new Date(startAt.getTime() + ((activity.duration || 60) * 60 * 1000)),
        };
      }).filter(activity => !Number.isNaN(activity.startAt.getTime()));
  } catch (error) {
    console.error("Failed to read custom activities for notifications:", error);
    return null;
  }
};

const normalizeTeamActivities = (activities) => activities.map((activity) => {
  const startAt = new Date(activity.scheduled_at);
  return {
    id: activity.id,
    title: activity.title,
    location: activity.location || "",
    duration: activity.duration || 60,
    type: activity.activity_type || 'practice',
    startAt,
    endAt: new Date(startAt.getTime() + ((activity.duration || 60) * 60 * 1000)),
  };
});

export const ActivityReminderManager = () => {
  const { user } = useUser();
  const isAthlete = user?.account_type === "athlete";
  const syncing = useRef(null);

  const syncReminders = useCallback(async () => {
    if (!user?.id || !isAthlete) return;
    const token = getSessionToken();
    if (!token || syncing.current === token) return;
    syncing.current = token;
    const config = { headers: { Authorization: `Bearer ${token}` } };

    try {
      if (Capacitor.getPlatform() !== 'android') {
        if (WebPushService.isSupported()) {
          await WebPushService.prepare(user.id);
          await WebPushService.restore(user.id);
          if (getSessionToken() !== token) return;
          // A new second device without a local agenda must not erase other items.
          if (localStorage.getItem('mygoal-custom-activities') !== null) {
            let sourceId = localStorage.getItem('mygoal-reminder-agenda-source');
            if (!sourceId) { sourceId = window.crypto.randomUUID(); localStorage.setItem('mygoal-reminder-agenda-source', sourceId); }
            const activities = getCustomActivitiesForUser(user.id);
            if (activities) await axios.put(`${API}/reminders/personal-activities`, { source_id: sourceId,
              activities: activities.map(activity => ({ id: activity.id,
                scheduled_at: activity.startAt.toISOString(), duration: activity.duration, activity_type: activity.type })) }, config);
          }
        }
        return;
      }
      const teamRes = await axios.get(`${API}/athlete/activities?athlete_id=${user.id}`, config);
      const [preferencesRes, sessionsRes, goalsRes] = await Promise.all([
        axios.get(`${API}/reminders/settings`, config), axios.get(`${API}/sessions?user_id=${user.id}`, config), axios.get(`${API}/goals?user_id=${user.id}`, config),
      ]);
      if (getSessionToken() !== token) return;
      const customActivities = getCustomActivitiesForUser(user.id) || [];
      const activities = [
        ...normalizeTeamActivities((teamRes.data || []).filter(activity => activity.current_attending !== false)),
        ...customActivities,
      ];

      await ActivityReminderService.syncActivityReminders({
        userId: user.id,
        activities,
        preferences: preferencesRes.data.preferences,
        ratedActivityIds: (sessionsRes.data || []).filter(s => s.rating).map(s => s.source_activity_id),
        goalActivityIds: (goalsRes.data || []).map(g => g.activity_id),
      });
    } catch (error) {
      console.error("Failed to sync athlete reminders:", error);
    } finally {
      if (syncing.current === token) syncing.current = null;
    }
  }, [isAthlete, user?.id]);

  useEffect(() => {
    if (!user?.id || !isAthlete) return undefined;

    syncReminders();

    const intervalId = window.setInterval(syncReminders, 5 * 60 * 1000);
    const handleFocus = () => syncReminders();
    const handleActivitiesUpdated = () => syncReminders();

    window.addEventListener("focus", handleFocus);
    window.addEventListener("storage", handleActivitiesUpdated);
    window.addEventListener("mygoal-activities-updated", handleActivitiesUpdated);
    window.addEventListener("mygoal-notifications-enabled", handleActivitiesUpdated);
    window.addEventListener("mygoal-reminders-updated", handleActivitiesUpdated);

    return () => {
      window.clearInterval(intervalId);
      window.removeEventListener("focus", handleFocus);
      window.removeEventListener("storage", handleActivitiesUpdated);
      window.removeEventListener("mygoal-activities-updated", handleActivitiesUpdated);
      window.removeEventListener("mygoal-notifications-enabled", handleActivitiesUpdated);
      window.removeEventListener("mygoal-reminders-updated", handleActivitiesUpdated);
    };
  }, [isAthlete, syncReminders, user?.id]);

  return null;
};

export default ActivityReminderManager;
